/**
 * Declarative layout for parent and student profile edit forms.
 * Edit group titles, footers, field order, and membership here — RoleProfileForm
 * renders these definitions via ProfileFormGroup.
 */
export type StandardProfileFieldKey =
  | "first_name"
  | "last_name"
  | "email"
  | "phone";

export type ProfileFormFieldDefinition =
  | { type: "standard"; key: StandardProfileFieldKey; label?: string }
  | { type: "custom"; fieldCode: string }
  | {
      type: "readonly_summary";
      /** i18n key under `profile.fields.*` */
      labelKey: "campusLevelClass";
      fieldCodes: readonly string[];
    };

export type ProfileFormGroupDefinition = {
  /** i18n key under `profile.groups.*` */
  titleKey:
    | "contact"
    | "familyPickup"
    | "billing"
    | "student"
    | "school"
    | "medical"
    | "familyPermissions";
  footer?: string;
  fields: ProfileFormFieldDefinition[];
};

/** Middle name — shared custom field on parent and student profiles. */
export const MIDDLE_NAME_FIELD_CODE =
  "770b2c6c8daeba8c83b1a46ddd46a68c8dbd1a26";

/** Student people field — which parent is the primary contact. */
export const PRIMARY_CONTACT_FIELD_CODE =
  "f243e160708c644e61c9b3869dfd92f59272ce47";

/** Student people field — which parent receives invoices. */
export const INVOICE_RECIPIENT_FIELD_CODE =
  "1e5031e794c7a5d8772bf73910f900be6ca023ea";

/** Student set field — weekdays the student may leave school without pickup. */
export const LEAVES_ALONE_FIELD_CODE =
  "0bf053c7ef333d473974f967c39caa2b72a0e79b";

/** Read-only school placement fields shown together as a single summary row. */
export const SCHOOL_PLACEMENT_FIELD_CODES = [
  "fcd31d529cc930e5af9852b79629cddf6f2bcbe4", // Campus
  "dc645fde1812f68ea0d377a7ed06b4da2dfafbe4", // Level
  "918a6c7a1bbdf589b0d9618523ba233135dba17f", // Class
] as const;

export const OTHER_PROFILE_FORM_GROUPS: ProfileFormGroupDefinition[] = [
  {
    titleKey: "contact",
    fields: [
      { type: "standard", key: "first_name" },
      { type: "standard", key: "last_name" },
      { type: "custom", fieldCode: MIDDLE_NAME_FIELD_CODE },
      { type: "standard", key: "email" },
      { type: "standard", key: "phone" },
    ],
  },
];

export const PARENT_PROFILE_FORM_GROUPS: ProfileFormGroupDefinition[] = [
  {
    titleKey: "contact",
    fields: [
      { type: "standard", key: "first_name" },
      { type: "standard", key: "last_name" },
      { type: "custom", fieldCode: MIDDLE_NAME_FIELD_CODE },
      { type: "standard", key: "email" },
      { type: "standard", key: "phone" },
    ],
  },
  {
    titleKey: "familyPickup",
    fields: [
      {
        type: "custom",
        fieldCode: "549e79dc9a0ea05d247715f0fe1ef3296fc0cd0d",
      }, // Child Lives with Parent
      {
        type: "custom",
        fieldCode: "fe25d20b90ce2dfcfeee079db86e46fec2ca3376",
      }, // Custody/Guardianship
      {
        type: "custom",
        fieldCode: "270e09bf6ebcee59de8587e71713f09ff1ab1854",
      }, // Permission to Pick-Up
    ],
  },
  {
    titleKey: "billing",
    fields: [
      {
        type: "custom",
        fieldCode: "c1db1893fb5820ae3c332a2783a905a7c33abd91",
      }, // Account Number
      {
        type: "custom",
        fieldCode: "b27de73342ce3ff140d82993acd03ccd242b1863",
      }, // Company Name
      {
        type: "custom",
        fieldCode: "eb0df8da182dfb4b003d6c46aa42fe2744df1762",
      }, // Company Address
      {
        type: "custom",
        fieldCode: "2432615926dad2d59ac24fbbd7f9bc949cee38e8",
      }, // IČ / DIČ
    ],
  },
];

export const STUDENT_PROFILE_FORM_GROUPS: ProfileFormGroupDefinition[] = [
  {
    titleKey: "student",
    fields: [
      { type: "standard", key: "first_name" },
      { type: "standard", key: "last_name" },
      { type: "custom", fieldCode: MIDDLE_NAME_FIELD_CODE },
    ],
  },
  {
    titleKey: "school",
    fields: [
      {
        type: "readonly_summary",
        labelKey: "campusLevelClass",
        fieldCodes: SCHOOL_PLACEMENT_FIELD_CODES,
      },
      {
        type: "custom",
        fieldCode: "953eeea522e19cf47ad0bf935f9ec89a02edc61e",
      }, // Residency Status
    ],
  },
  {
    titleKey: "medical",
    fields: [
      {
        type: "custom",
        fieldCode: "d50dfcd5ec71ae3dacd22d28981f65f3fb3e46ea",
      }, // Allergies (if any)
      {
        type: "custom",
        fieldCode: "4525c77eca10ee3e0fe3b71e94d7313987cf0d2b",
      }, // Medication/Medical Devices
      {
        type: "custom",
        fieldCode: "97374cfbb19d0e89ba88f3d1fda894715fcdfce5",
      }, // Dietary Restrictions
      {
        type: "custom",
        fieldCode: "951f53f35e0c55b236e72e3c47636276ce8bef5f",
      }, // Limitations for Activities
      {
        type: "custom",
        fieldCode: "1516bd4eaf819ab661baec40b43abc285fe1abcf",
      }, // Pediatrician Name
      {
        type: "custom",
        fieldCode: "4a5f1b9bf02793fe2de5ccd273b10a393081ca26",
      }, // Pediatrician Phone #
      {
        type: "custom",
        fieldCode: "c1b8b9b012e5cb8095a5a2c6dbdc3c30bc8eb18a",
      }, // Health Insurance Company
    ],
  },
  {
    titleKey: "familyPermissions",
    fields: [
      {
        type: "custom",
        fieldCode: PRIMARY_CONTACT_FIELD_CODE,
      },
      {
        type: "custom",
        fieldCode: LEAVES_ALONE_FIELD_CODE,
      },
      {
        type: "custom",
        fieldCode: INVOICE_RECIPIENT_FIELD_CODE,
      },
    ],
  },
];
